
import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Heart, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { getClientIdForServer } from '@/utils/fhirUtils';
import { importDataFrom } from '@/utils/fhirDataUtils';

const Source = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processing FHIR import...');
  const hasProcessed = useRef(false);

  // Helper function to get cookie value
  const getCookie = (name: string): string | null => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
    return null;
  };

  useEffect(() => {
    // Prevent multiple executions
    if (hasProcessed.current) {
      return;
    }
    hasProcessed.current = true;

    const handleOAuthCallback = async () => {
      try {
        console.log('Processing OAuth callback...');
        
        const code = searchParams.get('code');
        let state = searchParams.get('state');

        console.log('Code:', code);
        console.log('State:', state);

        // Handle case where state is missing (for eCW and North Fulton)
        if (code && !state) {
          state = getCookie('import-state') || "HHDCAA/24746157186194884";
          console.log('Using state from cookie:', state);
        }

        if (!code || !state) {
          setStatus('error');
          setMessage('Missing authorization code or state parameter');
          return;
        }

        // Parse state to get health system ID
        const stateParts = state.split('/');
        const healthSystemId = stateParts[0];

        console.log('Health system ID:', healthSystemId);

        // Get stored endpoint data from localStorage
        const storedEndpointData = localStorage.getItem(`endpoint_${healthSystemId}`);
        if (!storedEndpointData) {
          setStatus('error');
          setMessage('Health system data not found. Please try importing again.');
          return;
        }

        const endpoint = JSON.parse(storedEndpointData);
        console.log('Retrieved endpoint data:', endpoint);

        // Get stored metadata from localStorage
        const storedMetadata = localStorage.getItem(`metadata_${healthSystemId}`);
        if (!storedMetadata) {
          setStatus('error');
          setMessage('Health system metadata not found. Please try importing again.');
          return;
        }

        const metadata = JSON.parse(storedMetadata);
        console.log('Retrieved metadata:', metadata);

        // Find OAuth token endpoint
        const extensions = metadata.rest[0].security.extension;
        const oauthExtension = extensions.find((e: any) => e.url === "http://fhir-registry.smarthealthit.org/StructureDefinition/oauth-uris");
        
        if (!oauthExtension || !oauthExtension.extension) {
          setStatus('error');
          setMessage('OAuth configuration not found');
          return;
        }

        const tokenExtension = oauthExtension.extension.find((e: any) => e.url === 'token');
        if (!tokenExtension || !tokenExtension.valueUri) {
          setStatus('error');
          setMessage('Token endpoint not found');
          return;
        }

        let tokenUrl = tokenExtension.valueUri;
        let fhirAddress = endpoint.address;

        // Get client ID
        const clientId = getClientIdForServer(metadata, healthSystemId);
        if (!clientId) {
          setStatus('error');
          setMessage('Client ID not found for this health system');
          return;
        }

        console.log('Client ID:', clientId);
        console.log('Token URL:', tokenUrl);

        // Handle special cases for eCW
        if (tokenUrl === "https://connect.healow.com/apps/oauth/access_token") {
          tokenUrl = "https://edge.zpaper.com/r/wallet/ecw/access_token";
          fhirAddress = fhirAddress.replace("fhir.healow.com/FHIRServer/", "edge.zpaper.com/r/wallet/ecw/");
        } else if (tokenUrl === "https://oauthserver.eclinicalworks.com/oauth/oauth2/token") {
          tokenUrl = "https://edge.zpaper.com/r/fhir/access_token";
        }

        // Prepare token exchange request
        const redirectUri = `${window.location.protocol}//${window.location.host}/source`;
        let tokenRequestBody: any = {
          grant_type: "authorization_code",
          code: code,
          redirect_uri: redirectUri,
          client_id: clientId
        };

        // Add code verifier for specific clients
        if (clientId === "0oak11xvyobnlP0qL297") { // Athena
          tokenRequestBody.code_verifier = "M25iVXpKU3puUjFaYWg3T1NDTDQtcW1ROUY5YXlwalNoc0hhakxifmZHag";
        } else if (clientId === "JcjgosvOOHtRdThNyEsKndvuvLlQXfyKw_4XYwYns2Y" || clientId === "yP7GArxnmCK0FmIftjwD3kc94fxpeZ5fCDNAxm-7r0E" || clientId === "oO5OtwN1ZKqeg_VIEDoiNXbkkOIBRH0WrlCXWjPpJ-0" || clientId == "T1SRnrUeCc48fkBjOvFo9d0oOEQGitl9wzn1AD9p0CU" || clientId == "HVKi3Jrt9lUZGpWeayT-bgQUr2IoXHMNWGZdyVyxdUI") {
          tokenRequestBody.code_verifier = "MthLRt1dLUJFvx2GVti6zBKgoW6jyErEUELL1bvhFFxHcc2uyj6ymw_cY5wECRISTSIE81j9cVE1UKdP4LetvJ5zH47Wwa9GD1DC0Ptw9bz1HnTTq0ir_QWyf1Ocd_0f";
        }

        console.log('Exchanging code for token...');

        // Exchange authorization code for access token
        const tokenResponse = await fetch(tokenUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams(tokenRequestBody)
        });

        if (!tokenResponse.ok) {
          throw new Error(`Token exchange failed: ${tokenResponse.status}`);
        }

        const tokenData = await tokenResponse.json();
        console.log('Token exchange successful:', tokenData);

        setMessage('Token received, downloading medical records...');

        // Import FHIR data using the new utility function
        await importDataFrom(fhirAddress, tokenData);

        // Store the access token and FHIR endpoint for later use
        localStorage.setItem(`fhir_token_${healthSystemId}`, JSON.stringify({
          access_token: tokenData.access_token,
          refresh_token: tokenData.refresh_token,
          expires_in: tokenData.expires_in,
          fhir_endpoint: fhirAddress,
          health_system: endpoint.name,
          imported_at: new Date().toISOString()
        }));

        // Clean up temporary data
        localStorage.removeItem(`endpoint_${healthSystemId}`);
        localStorage.removeItem(`metadata_${healthSystemId}`);

        // Clear the import state cookie
        document.cookie = 'import-state=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';

        setStatus('success');
        setMessage(`Successfully imported medical records from ${endpoint.name}!`);

        // Redirect to dashboard after a short delay
        setTimeout(() => {
          navigate('/dashboard');
        }, 3000);

      } catch (error) {
        console.error('OAuth callback error:', error);
        setStatus('error');
        setMessage(`Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    };

    handleOAuthCallback();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            {status === 'processing' && <Heart className="h-12 w-12 text-primary animate-pulse" />}
            {status === 'success' && <CheckCircle className="h-12 w-12 text-green-500" />}
            {status === 'error' && <XCircle className="h-12 w-12 text-red-500" />}
          </div>
          <CardTitle>Medical Records Import</CardTitle>
          <CardDescription>
            {status === 'processing' && 'Processing your medical records import...'}
            {status === 'success' && 'Import completed successfully!'}
            {status === 'error' && 'Import failed'}
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            {status === 'processing' && <Loader2 className="h-4 w-4 animate-spin" />}
            <span className={
              status === 'error' ? 'text-destructive' : 
              status === 'success' ? 'text-green-600' : 
              'text-muted-foreground'
            }>
              {message}
            </span>
          </div>
          
          {status === 'error' && (
            <div className="mt-4 space-y-2">
              <button 
                onClick={() => navigate('/dashboard')}
                className="text-primary hover:underline block w-full"
              >
                Return to dashboard
              </button>
              <button 
                onClick={() => navigate('/')}
                className="text-muted-foreground hover:underline block w-full"
              >
                Go to homepage
              </button>
            </div>
          )}

          {status === 'success' && (
            <div className="mt-4">
              <p className="text-sm text-muted-foreground">
                Redirecting to dashboard in a few seconds...
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Source;
